import { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";

const MOBILE_WIDTH_QUERY = "(max-width: 768px)";
const VIEW_RESET_SELECTORS = [
    ".ant-modal-root",
    ".ant-modal-wrap",
    ".ant-select-dropdown",
    ".ant-picker-dropdown",
    ".ds-modal",
];

const isMobileViewport = () => {
    if (typeof window === "undefined") {
        return false;
    }

    if (typeof window.matchMedia === "function") {
        return window.matchMedia(MOBILE_WIDTH_QUERY).matches;
    }

    return window.innerWidth <= 768;
};

const resetHorizontalScroll = () => {
    if (!isMobileViewport() || typeof document === "undefined") {
        return;
    }

    const scrollTargets = [
        document.scrollingElement,
        document.documentElement,
        document.body,
    ].filter(Boolean);

    scrollTargets.forEach((target) => {
        target.scrollLeft = 0;
    });

    if (window.scrollX !== 0) {
        window.scrollTo(0, window.scrollY);
    }
};

const hasViewportChangingNode = (node) => {
    if (!(node instanceof Element)) {
        return false;
    }

    return VIEW_RESET_SELECTORS.some(
        (selector) => node.matches(selector) || node.querySelector(selector),
    );
};

const scheduleHorizontalReset = () => {
    resetHorizontalScroll();
    window.requestAnimationFrame(resetHorizontalScroll);
    window.setTimeout(resetHorizontalScroll, 260);
};

function MobileViewportGuard() {
    const location = useLocation();

    const reset = useCallback(() => {
        if (typeof window === "undefined") {
            return;
        }

        scheduleHorizontalReset();
    }, []);

    useEffect(() => {
        reset();
    }, [location.hash, location.pathname, location.search, reset]);

    useEffect(() => {
        if (typeof window === "undefined" || typeof document === "undefined") {
            return undefined;
        }

        const events = ["pageshow", "resize", "orientationchange"];
        events.forEach((eventName) => {
            window.addEventListener(eventName, reset);
        });
        window.visualViewport?.addEventListener("resize", reset);

        const observer = new MutationObserver((mutations) => {
            const shouldReset = mutations.some((mutation) =>
                [...mutation.addedNodes, ...mutation.removedNodes].some(
                    hasViewportChangingNode,
                ),
            );

            if (shouldReset) {
                reset();
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => {
            events.forEach((eventName) => {
                window.removeEventListener(eventName, reset);
            });
            window.visualViewport?.removeEventListener("resize", reset);
            observer.disconnect();
        };
    }, [reset]);

    return null;
}

export default MobileViewportGuard;
